import React from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { Breadcrumbs } from './Breadcrumb';
import { Grid, GridCell } from './Grid';
import breakpoints from './Theme/breakpoints';
import InnerHtmlStyling from './InnerHtmlStyling';
import Headline1 from './Typography/Heading/Headline1';
import Body1 from './Typography/Body/Body1';

interface ContentProperties {
  withTableStyles?: boolean;
  children: React.ReactNode;
}

export const BreadcrumbsWrapper = styled.div`
  display: flex;
  justify-content: start;
  flex-basis: 100%;
  padding-bottom: 2rem;
`;

const Header = styled(Headline1)`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const PageWrapper = styled.div`
  margin: 0 auto 32px;
  max-width: 970px;
  box-sizing: border-box;
  width: 100%;
  padding: 0 16px;

  .overflow-scroll table {
    width: 100%;
  }

  .overflow-scroll table tr {
    text-align: center;
  }

  .overflow-scroll {
    overflow-x: scroll;
  }

  @media (min-width: ${breakpoints.lg}px) {
    .overflow-scroll {
      overflow-x: initial;
    }
  }

  @media (min-width: ${breakpoints.lg}px) {
    margin: 0 auto 48px;
    padding: 0;
  }
`;

const Content = styled(InnerHtmlStyling, {
  shouldForwardProp: (property) => property !== 'withTableStyles',
})<ContentProperties>`
  font-family: ${({ theme }) => theme.fonts.primary};

  .text-gray-300 {
    font-size: 0.72rem;
  }
`;

const SubTitle = styled(Body1)`
  color: ${({ theme }) => theme.palette.primary.main};
  font-weight: 600;
  text-align: center;
  margin-bottom: .5rem;
`;

const staticPagePropertyTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
    ]),
  ).isRequired,
  content: PropTypes.node.isRequired,
  subTitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  withTableStyles: PropTypes.bool,
  disclaimer: PropTypes.node,
  children: PropTypes.node,
};

type StaticPageProperties = InferProps<
  typeof staticPagePropertyTypes
> & {
  withTableStyles?: boolean;
};

const StaticPage = ({
  breadcrumbs,
  content,
  subTitle,
  title,
  withTableStyles,
  disclaimer,
  children,
}: StaticPageProperties) => (
  <Grid>
    <GridCell padding="0">
      <PageWrapper>
        <BreadcrumbsWrapper>
          <Breadcrumbs crumbs={breadcrumbs} />
        </BreadcrumbsWrapper>
        {subTitle && (
          <SubTitle>{subTitle}</SubTitle>
        )}
        <Header tag="h1">{title}</Header>
        <Content withTableStyles={withTableStyles}>
          {content}
        </Content>
        {children}
        <Content>
          {disclaimer}
        </Content>
      </PageWrapper>
    </GridCell>
  </Grid>
);

StaticPage.propTypes = staticPagePropertyTypes;

export default StaticPage;
